import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Modal,
  Progress,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { adminApi, formatApiError, type PageResult } from '../../api'
import { KNOWLEDGE_SCOPE_OPTIONS_SHORT } from '../../constants/knowledgeScope'
import { PageHeader, tablePagination, useServerPagination, formatDate } from '../../components/ui'
import './QualitySettings.css'

type PipelineJob = {
  id: number
  name: string
  scope: string
  source_type: string
  dataset_id: number | null
  item_count: number
  steps: {
    index: boolean
    clean: boolean
    desensitize: boolean
    extract_attrs: boolean
    extract_graph: boolean
  }
  status: string
  progress: number
  total: number
  done_count: number
  failed_count: number
  current_step: string
  status_message: string
  last_error: string
  created_by_name: string
  created_at: string
  started_at: string | null
  finished_at: string | null
}

type DatasetOpt = { id: number; name: string; scope: string }

const STATUS_COLOR: Record<string, string> = {
  idle: 'default',
  running: 'processing',
  completed: 'success',
  error: 'error',
}

const STATUS_LABEL: Record<string, string> = {
  idle: '待执行',
  running: '执行中',
  completed: '已完成',
  error: '失败',
}

const SCOPE_LABEL: Record<string, string> = Object.fromEntries(
  KNOWLEDGE_SCOPE_OPTIONS_SHORT.map((o) => [o.value, o.label]),
)

function stepsLabel(steps: PipelineJob['steps']) {
  const parts: string[] = []
  if (steps.desensitize) parts.push('脱敏')
  if (steps.index) parts.push('建索引')
  if (steps.clean) parts.push('评质量')
  if (steps.extract_attrs) parts.push('抽属性')
  if (steps.extract_graph) parts.push('抽图谱')
  return parts.join(' → ') || '—'
}

function stepsTags(steps: PipelineJob['steps']) {
  const defs: { key: keyof PipelineJob['steps']; label: string }[] = [
    { key: 'desensitize', label: '数据脱敏' },
    { key: 'index', label: '重新切片并建索引' },
    { key: 'clean', label: '评估文档质量' },
    { key: 'extract_attrs', label: '抽取文档属性' },
    { key: 'extract_graph', label: '抽取知识图谱关系' },
  ]
  const enabled = defs.filter(({ key }) => steps[key])
  if (!enabled.length) return '—'
  return (
    <Space wrap size={[4, 4]}>
      {enabled.map(({ key, label }) => (
        <Tag key={key} color="blue">
          {label}
        </Tag>
      ))}
    </Space>
  )
}

export default function PipelineJobs() {
  const [jobs, setJobs] = useState<PipelineJob[]>([])
  const [total, setTotal] = useState(0)
  const [datasets, setDatasets] = useState<DatasetOpt[]>([])
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const [viewJobId, setViewJobId] = useState<number | null>(null)
  const [viewDetail, setViewDetail] = useState<PipelineJob | null>(null)
  const [viewLoading, setViewLoading] = useState(false)
  const [form] = Form.useForm()
  const { page, pageSize, handlePaginationChange, paginationOptions } = useServerPagination()

  const datasetLabelMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const d of datasets) {
      const scopeLabel = SCOPE_LABEL[d.scope] || d.scope
      map.set(d.id, `${d.name}（${scopeLabel}）`)
    }
    return map
  }, [datasets])

  const load = useCallback(() => {
    adminApi
      .get<PageResult<PipelineJob>>('/admin/pipeline/jobs', {
        params: { page, page_size: pageSize },
      })
      .then((r) => {
        setJobs(r.data.items || [])
        setTotal(r.data.total || 0)
      })
      .catch(() => {})
  }, [page, pageSize])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    adminApi
      .get<{ items: DatasetOpt[] }>('/admin/datasets/options', { params: { published_only: false } })
      .then((r) => setDatasets(r.data.items || []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!jobs.some((j) => j.status === 'running')) return
    const t = window.setInterval(load, 3000)
    return () => window.clearInterval(t)
  }, [jobs, load])

  const loadViewDetail = useCallback((id: number) => {
    setViewLoading(true)
    adminApi
      .get<PipelineJob>(`/admin/pipeline/jobs/${id}`)
      .then((r) => setViewDetail(r.data))
      .catch((e) => message.error(formatApiError(e, '加载详情失败')))
      .finally(() => setViewLoading(false))
  }, [])

  useEffect(() => {
    if (!viewOpen || viewJobId == null || viewDetail?.status !== 'running') return
    const t = window.setInterval(() => loadViewDetail(viewJobId), 3000)
    return () => window.clearInterval(t)
  }, [viewOpen, viewJobId, viewDetail?.status, loadViewDetail])

  const openView = (row: PipelineJob) => {
    setViewJobId(row.id)
    setViewDetail(row)
    setViewOpen(true)
    loadViewDetail(row.id)
  }

  const closeView = () => {
    setViewOpen(false)
    setViewJobId(null)
    setViewDetail(null)
  }

  const createJob = () => {
    void form.validateFields().then((v) => {
      setCreating(true)
      adminApi
        .post<PipelineJob>('/admin/pipeline/jobs', {
          name: v.name,
          source_type: 'dataset',
          dataset_id: v.dataset_id,
          steps: {
            desensitize: !!v.step_desensitize,
            index: !!v.step_index,
            clean: !!v.step_clean,
            extract_attrs: !!v.step_attrs,
            extract_graph: !!v.step_graph,
          },
        })
        .then((r) => {
          message.success('任务已创建')
          setOpen(false)
          form.resetFields()
          load()
          if (v.run_now) {
            return adminApi.post(`/admin/pipeline/jobs/${r.data.id}/run`).then(() => {
              message.success('已开始执行')
              load()
            })
          }
        })
        .catch((e) => message.error(formatApiError(e, '创建失败')))
        .finally(() => setCreating(false))
    })
  }

  const runJob = (id: number) => {
    adminApi
      .post(`/admin/pipeline/jobs/${id}/run`)
      .then(() => {
        message.success('已开始执行')
        load()
      })
      .catch((e) => message.error(formatApiError(e, '执行失败')))
  }

  return (
    <div>
      <PageHeader
        title="批量加工任务"
        description="对某个数据集里的文档，一次性批量建索引、评质量、抽属性或抽图谱。日常入库一般不用来这里，仅在需要补跑历史文档时使用。"
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="什么时候需要用？"
        description={
          <ul className="quality-settings-intro">
            <li>
              <strong>不需要</strong>：新文档正常上传后，系统会自动建索引并评估质量（需在「文档质量评估」中开启）。
            </li>
            <li>
              <strong>需要</strong>：改了切片规则、质量规则，或有一批老文档从未建索引/评质量，想对整个数据集批量重跑。
            </li>
            <li>
              <strong>和「文档质量评估」的区别</strong>：质量评估页是设规则；本页是按规则对已有文档<strong>批量执行</strong>。
            </li>
          </ul>
        }
      />

      <Card>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            新建任务
          </Button>
        </div>
        <Table<PipelineJob>
          rowKey="id"
          dataSource={jobs}
          pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
          columns={[
            { title: '任务名称', dataIndex: 'name', ellipsis: true },
            {
              title: '数据集',
              width: 180,
              ellipsis: true,
              render: (_, r) =>
                r.dataset_id
                  ? datasetLabelMap.get(r.dataset_id) || `#${r.dataset_id}`
                  : `${r.item_count} 篇`,
            },
            {
              title: '处理步骤',
              width: 160,
              ellipsis: true,
              render: (_, r) => stepsLabel(r.steps),
            },
            {
              title: '状态',
              dataIndex: 'status',
              width: 88,
              render: (s: string) => <Tag color={STATUS_COLOR[s] || 'default'}>{STATUS_LABEL[s] || s}</Tag>,
            },
            {
              title: '进度',
              width: 160,
              render: (_, r) =>
                r.status === 'running' || r.progress > 0 ? (
                  <Progress percent={r.progress} size="small" status={r.status === 'error' ? 'exception' : 'active'} />
                ) : (
                  '—'
                ),
            },
            {
              title: '说明',
              dataIndex: 'status_message',
              ellipsis: true,
            },
            {
              title: '创建时间',
              dataIndex: 'created_at',
              width: 168,
              render: (v: string) => formatDate(v),
            },
            {
              title: '操作',
              width: 196,
              render: (_, r) => (
                <Space size="small" wrap>
                  <Button size="small" onClick={() => openView(r)}>
                    查看
                  </Button>
                  <Button size="small" disabled={r.status === 'running'} onClick={() => runJob(r.id)}>
                    {r.status === 'idle' ? '执行' : '重跑'}
                  </Button>
                  <Button
                    size="small"
                    danger
                    disabled={r.status === 'running'}
                    onClick={() =>
                      adminApi.delete(`/admin/pipeline/jobs/${r.id}`).then(() => {
                        message.success('已删除')
                        load()
                      })
                    }
                  >
                    删除
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="新建批量加工任务"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={createJob}
        confirmLoading={creating}
        width={520}
        okText="创建"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            step_desensitize: false,
            step_index: true,
            step_clean: true,
            step_attrs: false,
            step_graph: false,
            run_now: true,
          }}
        >
          <Form.Item name="name" label="任务名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如：HR 制度库补跑索引" />
          </Form.Item>
          <Form.Item name="dataset_id" label="数据集" rules={[{ required: true, message: '请选择数据集' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={datasets.map((d) => ({
                value: d.id,
                label: datasetLabelMap.get(d.id) || d.name,
              }))}
              placeholder="选择要处理的数据集"
            />
          </Form.Item>
          <Form.Item label="要执行的操作">
            <Space direction="vertical">
              <Form.Item name="step_desensitize" valuePropName="checked" noStyle>
                <Checkbox>数据脱敏（分片后写入 ES 前脱敏，需同时建索引）</Checkbox>
              </Form.Item>
              <Form.Item name="step_index" valuePropName="checked" noStyle>
                <Checkbox>重新切片并建索引</Checkbox>
              </Form.Item>
              <Form.Item name="step_clean" valuePropName="checked" noStyle>
                <Checkbox>评估文档质量（高/中/低 + 是否重复）</Checkbox>
              </Form.Item>
              <Form.Item name="step_attrs" valuePropName="checked" noStyle>
                <Checkbox>属性抽取（需已开启）</Checkbox>
              </Form.Item>
              <Form.Item name="step_graph" valuePropName="checked" noStyle>
                <Checkbox>图谱关系抽取</Checkbox>
              </Form.Item>
            </Space>
          </Form.Item>
          <Form.Item name="run_now" valuePropName="checked">
            <Checkbox>创建后立即执行</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={viewDetail ? `任务详情 · ${viewDetail.name}` : '任务详情'}
        open={viewOpen}
        onCancel={closeView}
        footer={
          viewDetail ? (
            <Space>
              <Button onClick={closeView}>关闭</Button>
              <Button
                type="primary"
                disabled={viewDetail.status === 'running'}
                onClick={() => {
                  runJob(viewDetail.id)
                  loadViewDetail(viewDetail.id)
                }}
              >
                {viewDetail.status === 'idle' ? '执行' : '重跑'}
              </Button>
            </Space>
          ) : null
        }
        width={640}
        destroyOnHidden
      >
        {viewDetail ? (
          <Spin spinning={viewLoading}>
            <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="任务名称">{viewDetail.name}</Descriptions.Item>
            <Descriptions.Item label="知识库">
              {SCOPE_LABEL[viewDetail.scope] || viewDetail.scope}
            </Descriptions.Item>
            <Descriptions.Item label="数据来源">
              {viewDetail.dataset_id
                ? datasetLabelMap.get(viewDetail.dataset_id) || `数据集 #${viewDetail.dataset_id}`
                : `手动指定 ${viewDetail.item_count} 篇`}
            </Descriptions.Item>
            <Descriptions.Item label="文档数">{viewDetail.item_count}</Descriptions.Item>
            <Descriptions.Item label="处理步骤">{stepsTags(viewDetail.steps)}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={STATUS_COLOR[viewDetail.status] || 'default'}>
                {STATUS_LABEL[viewDetail.status] || viewDetail.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="进度">
              {viewDetail.status === 'running' || viewDetail.progress > 0 ? (
                <Progress
                  percent={viewDetail.progress}
                  size="small"
                  status={viewDetail.status === 'error' ? 'exception' : 'active'}
                />
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="执行统计">
              成功 {viewDetail.done_count} / 失败 {viewDetail.failed_count} / 共 {viewDetail.total}
            </Descriptions.Item>
            {viewDetail.current_step ? (
              <Descriptions.Item label="当前步骤">{viewDetail.current_step}</Descriptions.Item>
            ) : null}
            <Descriptions.Item label="说明">
              <Typography.Text>{viewDetail.status_message || '—'}</Typography.Text>
            </Descriptions.Item>
            {viewDetail.last_error ? (
              <Descriptions.Item label="最近错误">
                <Typography.Text type="danger">{viewDetail.last_error}</Typography.Text>
              </Descriptions.Item>
            ) : null}
            <Descriptions.Item label="创建人">{viewDetail.created_by_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="创建时间">{formatDate(viewDetail.created_at)}</Descriptions.Item>
            <Descriptions.Item label="开始时间">
              {viewDetail.started_at ? formatDate(viewDetail.started_at) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="结束时间">
              {viewDetail.finished_at ? formatDate(viewDetail.finished_at) : '—'}
            </Descriptions.Item>
          </Descriptions>
          </Spin>
        ) : null}
      </Modal>
    </div>
  )
}
